'use client';

import React, { type ReactNode } from 'react';
import { cn } from '@sharpr/ui';

/** Tone of the *unselected* chip. `default` = neutral; `questionable` = amber
 *  (a team with questionable players). The selected state is
 *  tone-invariant (always brand). */
type FilterChipTone = 'default' | 'questionable';

interface FilterChipProps {
  /** Toggle state — selected renders the brand fill; unselected uses `tone`. */
  isSelected: boolean;
  onClick: () => void;
  /** Unselected tone (ignored while selected). */
  tone?: FilterChipTone;
  title?: string;
  /** Extra classes; tailwind-merged so callers can override base utilities. */
  className?: string;
  children: ReactNode;
}

/**
 * Shell + size for any filter pill — layout, the touch-target density split
 * (roomy on mobile, compact on desktop ≥md 768px), shape, border, and the
 * shared interaction states. Excludes `gap` (varies) and colors (state-specific)
 * so consumers compose those on top. Single source of truth for chip sizing —
 * change density HERE and every filter chip follows. Used by `FilterChip` and
 * the Conditionals filter's chips.
 */
export const FILTER_CHIP_BASE =
  'inline-flex items-center px-2 py-1.5 min-h-[28px] md:py-1 md:min-h-0 text-[11px] font-medium rounded-full border whitespace-nowrap transition-all duration-200 hover:shadow-md active:scale-95 outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 [-webkit-tap-highlight-color:transparent]';

const UNSELECTED_TONE: Record<FilterChipTone, string> = {
  default:
    'bg-white dark:bg-bg-tertiary text-gray-600 dark:text-text-secondary border-gray-200 dark:border-border-primary hover:border-gray-400 dark:hover:border-border-interactive hover:bg-gray-50 dark:hover:bg-bg-overlay',
  questionable:
    'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/50',
};

/**
 * Shared pill for the local Teams / Markets filters. Single source of truth for
 * chip sizing — including the touch-target density split (roomy tap target on
 * mobile, compact on desktop ≥md) — plus border, hover, focus, and the selected
 * (brand) vs unselected (neutral / questionable-amber) states. Change chip sizing
 * HERE; every filter chip inherits it.
 */
export function FilterChip({
  isSelected,
  onClick,
  tone = 'default',
  title,
  className,
  children,
}: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={isSelected}
      className={cn(
        FILTER_CHIP_BASE,
        'gap-1',
        isSelected
          ? 'bg-brand-500 dark:bg-brand-600 text-white border-brand-500 dark:border-brand-600 shadow-sm hover:bg-brand-600 dark:hover:bg-brand-500'
          : UNSELECTED_TONE[tone],
        className,
      )}
    >
      {children}
    </button>
  );
}
